/**
 * Policy Repository
 */

import { BaseRepository } from './BaseRepository';
import { Policy } from '../types/WorkflowEntities';

class PolicyRepository extends BaseRepository<Policy> {
    protected entityName = 'policy';
    protected graphQLType = 'Policy';

    // ✅ FERP SAFETY: Mandatory repository pattern method with tenant isolation
    async findByTenantAndId(tenantId: string, id: string): Promise<Policy | null> {
        await this.validateRepositorySafety({
            operation: 'FIND_BY_TENANT_AND_ID',
            domain: 'OPERATIONAL',
            tenantId,
            entityType: 'Policy',
            criticalityLevel: 'HIGH', // Policies are critical for compliance
            metadata: { id, policyRepository: true }
        });

        const query = `
            query GetPolicyByTenantAndId($id: String!, $tenant: String!) {
                policy(id: $id, tenant: $tenant) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        try {
            const result = await this.query<{ policy: Policy | null }>(query, {
                id,
                tenant: tenantId
            }, {
                cache: true,
                cacheTime: 10 * 60 * 1000 // 10 minutes cache for policies
            });

            const policy = result.policy || null;

            // ✅ FERP SAFETY: Log successful operation with policy details
            if (this.auditTrail) {
                await this.auditTrail.logOperationSuccess({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'Policy',
                    entityId: id,
                    found: !!policy,
                    policyType: policy?.type,
                    policyScope: policy?.scope,
                    policyEnabled: policy?.enabled,
                    timestamp: Date.now()
                });
            }

            return policy;
        } catch (error) {
            // ✅ FERP SAFETY: Log failed operation
            if (this.auditTrail) {
                await this.auditTrail.logOperationFailure({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'Policy',
                    entityId: id,
                    error: error instanceof Error ? error.message : String(error),
                    timestamp: Date.now()
                });
            }
            throw error;
        }
    }

    // Legacy findById method - redirects to findByTenantAndId
    async findById(policyId: string): Promise<Policy | null> {
        return this.findByTenantAndId(this.tenant, policyId);
    }

    // Policy-specific methods
    async testPolicy(policyId: string, testData: any): Promise<any> {
        const query = `
            mutation TestPolicy($policyId: ID!, $testData: JSON!) {
                testPolicy(policyId: $policyId, testData: $testData) {
                    success
                    violations {
                        rule
                        severity
                        message
                    }
                    score
                }
            }
        `;

        return this.executeQuery(query, { policyId, testData });
    }

    async findByScope(scope: string): Promise<Policy[]> {
        const query = `
            query FindPoliciesByScope($scope: String!) {
                policies(filter: { scope: $scope }) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(query, { scope });
        return result.policies || [];
    }

    async findByType(type: string): Promise<Policy[]> {
        const query = `
            query FindPoliciesByType($type: String!) {
                policies(filter: { type: $type }) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(query, { type });
        return result.policies || [];
    }

    protected getFieldSelection(): string {
        return `
            id
            name
            description
            type
            rules {
                id
                name
                expression
                severity
                message
                enabled
            }
            enabled
            priority
            scope
            conditions {
                field
                operator
                value
            }
            actions {
                type
                config
            }
            createdAt
            updatedAt
        `;
    }
}

export const policyRepository = new PolicyRepository();
export { PolicyRepository };